import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Modal,
  IconButton,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { gsap } from "gsap";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { masterlayoutbg } from "../../../../../assets";

// ✅ Structured Floor Plan Data (grouped by BHK)
const floorPlansData = [
  {
    id: 1,
    bhk: "5BHK",
    size: "7972",
    facings: [
      {
        name: "East Facing",
        image: "https://dprstorage.b-cdn.net/RW/projects/miraimist/7%2C972.25%20SFT.png",
        downloadImage: "https://dprstorage.b-cdn.net/RW/projects/miraimist/7%2C972.25%20SFT.png",
        details: "",
      },
    ],
  },
    {
    id: 2,
    bhk: "5BHK",
    size: "8036",
    facings: [
      {
        name: "East Facing",
        image: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C036.25%20SFT%20%20.png",
        downloadImage: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C036.25%20SFT%20%20.png",
        details: "",
      },
    ],
  },
    {
    id: 3,
    bhk: "5BHK",
    size: "8394",
    facings: [
      {
        name: "East Facing",
        image: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C394.25%20SFT.png",
        downloadImage: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C394.25%20SFT.png",
        details: "",
      },
    ],
  },
  {
    id: 4,
    bhk: "5BHK",
    size: "8403",
    facings: [
      {
        name: "West Facing",
        image: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C403.25%20SFT.png",
        downloadImage: "https://dprstorage.b-cdn.net/RW/projects/miraimist/8%2C403.25%20SFT.png",
        details: "",
      },
    ],
  },
  {
    id: 5,
    bhk: "5BHK",
    size: "10726",
    facings: [
      {
        name: "West Facing",
        image: "https://dprstorage.b-cdn.net/RW/projects/miraimist/10%2C726.25%20SFT.png",
        downloadImage: "https://dprstorage.b-cdn.net/RW/projects/miraimist/10%2C726.25%20SFT.png",
        details: "",
      },
    ],
  },


];

// Replace the old component declaration with this updated one that accepts a title prop
interface MiraiMistFloorPlansProps {
  title?: string;
}

const MiraiMistFloorPlans: React.FC<MiraiMistFloorPlansProps> = ({ title }) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLDivElement | null>(null);

  const [selectedBHK, setSelectedBHK] = useState(floorPlansData[0]);
  const [selectedFacing, setSelectedFacing] = useState(
    floorPlansData[0].facings[0]
  );
  const [openModal, setOpenModal] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Animate on change
  useEffect(() => {
    if (textRef.current) {
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );
    }
    if (imageRef.current) {
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out", delay: 0.3 }
      );
    }
  }, [selectedFacing]);

  // Keep selectedFacing in sync when selectedBHK changes externally
  useEffect(() => {
    if (!selectedBHK.facings.includes(selectedFacing)) {
      setSelectedFacing(selectedBHK.facings[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBHK]);



  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = selectedFacing.downloadImage;
    link.download = `${selectedBHK.bhk}-${selectedFacing.name}.jpg`;
    link.click();
  };


  const handlePrevNext = (dir: "prev" | "next") => {
    const currentIndex = floorPlansData.findIndex(
      (p) => p.id === selectedBHK.id
    );
    const newIndex =
      dir === "prev"
        ? (currentIndex - 1 + floorPlansData.length) % floorPlansData.length
        : (currentIndex + 1) % floorPlansData.length;
    const newBHK = floorPlansData[newIndex];
    setSelectedBHK(newBHK);
    setSelectedFacing(newBHK.facings[0]);
  };
  

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        backgroundImage: `url(${masterlayoutbg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0,0,0,0.6)",
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, py: 8 }}>
        {/* Title */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h4" fontWeight={600} color="white">
            {title || "PROMENADE FLOOR PLANS"}
          </Typography>
          <Divider
            sx={{
              width: { xs: "300px", md: "400px" },
              height: "1px",
              backgroundColor: "white",
              mx: "auto",
              my: 2,
            }}
          />
        </Box>

        {/* BHK Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: "8px",
            // ensure children center on desktop and wrap into rows on mobile
          }}
        >
          {floorPlansData.map((plan) => (
            <Button
              key={plan.id}
              variant="contained"
              onClick={() => {
                setSelectedBHK(plan);
                setSelectedFacing(plan.facings[0]);
              }}
              sx={{
                background:
                  selectedBHK.id === plan.id ? "#14658B" : "transparent",
                color: "white",
                border: "1px solid white",
                // px: 4,
                py: 2,
                borderRadius: 0,
                fontWeight: 700,
                "&:hover": {
                  background: "#14658B",
                },
                textTransform: "none",
                fontSize: { xs: 14, md: 18 },
                // Key layout change:
                // - On mobile: make each button take 50% width minus gap so two appear per row
                // - On desktop: auto width (keeps current layout)
                width: { xs: "calc(50% - 8px)", md: "auto" },
                // Make text centered inside the 50% width buttons
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {plan.bhk} – {plan.size} SQ.FT
            </Button>
          ))}
        </Box>

        {/* Facing Tabs */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          {selectedBHK.facings.map((facing) => (
            <Typography
              variant="h5"
              key={facing.name}
              onClick={() => setSelectedFacing(facing)}
              sx={{
                cursor: "pointer",
                color:
                  selectedFacing.name === facing.name ? "white" : "#bbb",
                fontWeight:
                  selectedFacing.name === facing.name ? 700 : 500,
                borderBottom:
                  selectedFacing.name === facing.name
                    ? "2px solid #14658B"
                    : "2px solid transparent", 
                pb: "4px",
                transition: "all 0.3s ease",
                fontSize: { xs: 14, md: 18}
              }}
            >
              {facing.name.toUpperCase()}
            </Typography>
          ))}
        </Box>

        {/* Image */}
        <Box
          ref={imageRef}
          sx={{
            textAlign: "center",
            width: "100%",
            maxWidth: "800px",
            mx: "auto",
            position: "relative",
          }}
        >
          <img
            src={selectedFacing.image}
            alt={`${selectedBHK.bhk} ${selectedFacing.name}`}
            style={{
              width: "92%",
              objectFit: "contain",
              borderRadius: 8,
              border: "1px solid #fff",
              padding: "15px",
              boxShadow: "0 0 15px rgba(15,99,165,0.7)",
              cursor: "pointer",
            }}
            onClick={() => setOpenModal(true)}
          />
        </Box>

        {/* Details */}
        <Box ref={textRef} sx={{ textAlign: "center", mt: 4 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: 18,
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              color: "white",
            }}
          >
            {selectedFacing.details}
          </Typography>
        </Box>

        {/* Modal */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              width: "100%",
              py: "60px",
              px: { xs: "16px", md: "0px" },
              backgroundColor: "rgba(0,0,0,0.95)",
              position: "relative",
            }}
          >
            <IconButton
              sx={{ position: "absolute", top: 20, right: 20, color: "white" }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon sx={{ fontSize: 40 }} />
            </IconButton>
            <IconButton
              sx={{ position: "absolute", top: 20, left: 20, color: "white" }}
              onClick={handleDownload}
            >
              <DownloadIcon sx={{ fontSize: 40 }} />
            </IconButton>

            {/* Prev / Next */}
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                left: { xs: 10, md: 20 },
                color: "white",
                transform: "translateY(-50%)",
              }}
              onClick={() => handlePrevNext("prev")}
            >
              <ArrowBackIosIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
            </IconButton>
            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                right: { xs: 10, md: 20 },
                color: "white",
                transform: "translateY(-50%)",
              }}
              onClick={() => handlePrevNext("next")}
            >
              <ArrowForwardIosIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
            </IconButton>

            <Box
              component="img"
              src={selectedFacing.downloadImage}
              alt="Full Floor Plan"
              sx={{
                maxWidth: "90%",
                maxHeight: "calc(100vh - 120px)",
                borderRadius: 2,
                boxShadow: "0 0 30px rgba(255,255,255,0.3)",
                objectFit: "contain",
              }}
            />
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default MiraiMistFloorPlans;
